import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, ExternalLink, FileText, Lock, Pencil, Plus, RotateCcw, Scale, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, get, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { ConfirmDelete, PageHeader, StatusBadge } from "@/admin/ui";
import { useRefresh } from "@/admin/hooks";

const usePageActions = () => {
  const refresh = useRefresh();
  const restore = useMutation({ mutationFn: (p) => api.post(`/admin/pages/${p.id}/restore`), onSuccess: () => { toast.success("Página restaurada"); refresh(); }, onError: (e) => toast.error(errMsg(e)) });
  const trash = useMutation({
    mutationFn: (p) => api.delete(`/admin/pages/${p.id}`),
    onSuccess: (_, p) => { refresh(); toast.success("Página movida a la papelera", { action: { label: "Deshacer", onClick: () => restore.mutate(p) } }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const purge = useMutation({ mutationFn: (p) => api.delete(`/admin/pages/${p.id}/permanent`), onSuccess: () => { toast.success("Página eliminada para siempre"); refresh(); }, onError: (e) => toast.error(errMsg(e)) });
  return { restore, trash, purge };
};

const Row = ({ p, children }) => (
  <div className="flex flex-wrap sm:flex-nowrap items-center gap-x-4 gap-y-2 px-5 py-4 border-b border-border last:border-0" data-testid={`page-row-${p.slug || p.key}`}>
    <div className="min-w-0 flex-1">
      <p className="font-display font-bold text-lg leading-snug truncate">{p.kind === "content" ? p.nav_label || p.title : p.title}</p>
      <p className="text-xs text-muted-foreground font-mono truncate">{p.path}{p.deleted_at ? ` · en la papelera desde ${fmtDateTime(p.deleted_at)}` : ""}</p>
    </div>
    {p.kind === "content" && <StatusBadge status={p.status} testid={`page-status-${p.slug}`} />}
    <div className="flex items-center gap-1">{children}</div>
  </div>
);

const LiveActions = ({ p, actions }) => (
  <>
    {p.kind === "content" && p.status === "published" && (
      <Button size="icon" variant="ghost" asChild><a href={p.path} target="_blank" rel="noopener noreferrer" aria-label="Ver en la web" data-testid={`page-view-${p.slug}`}><ExternalLink /></a></Button>
    )}
    <Button size="icon" variant="ghost" asChild><Link to={`/admin/paginas/${p.id}`} aria-label="Editar" data-testid={`page-edit-${p.slug || p.key}`}><Pencil /></Link></Button>
    {p.deletable ? (
      <Button size="icon" variant="ghost" className="hover:text-destructive" onClick={() => actions.trash.mutate(p)} aria-label="Eliminar" data-testid={`page-delete-${p.slug || p.key}`}><Trash2 /></Button>
    ) : (
      <span className="h-9 w-9 grid place-items-center text-muted-foreground/60" title="Forma parte de la estructura del periódico: se puede editar, pero no eliminar" data-testid={`page-locked-${p.key}`}><Lock className="h-4 w-4" /></span>
    )}
  </>
);

const TrashActions = ({ p, actions }) => (
  <>
    <Button size="sm" variant="outline" className="bg-card" onClick={() => actions.restore.mutate(p)} data-testid={`page-restore-${p.slug || p.key}`}><RotateCcw /> Restaurar</Button>
    {p.kind === "content" && (
      <ConfirmDelete title="¿Eliminar para siempre?" description={`«${p.nav_label || p.title}» se borrará definitivamente y no podrás recuperarla.`} onConfirm={() => actions.purge.mutate(p)} testid={`page-purge-confirm-${p.slug}`}>
        <Button size="sm" variant="ghost" className="hover:text-destructive" data-testid={`page-purge-${p.slug}`}><Trash2 /> Eliminar</Button>
      </ConfirmDelete>
    )}
  </>
);

const Group = ({ title, icon: Icon, items, render, testid }) => items.length > 0 && (
  <section className="mb-8" data-testid={testid}>
    <p className="kicker text-[10px] text-muted-foreground mb-3 flex items-center gap-2"><Icon className="h-3.5 w-3.5" />{title}</p>
    <div className="bg-card border border-border">{items.map(render)}</div>
  </section>
);

const LegalWarning = () => {
  const { data } = useQuery({ queryKey: ["admin-settings"], queryFn: () => get("/admin/settings") });
  if (!data || (data.legal_name && data.legal_nif && data.legal_address && data.legal_email)) return null;
  return (
    <div className="mb-8 flex flex-wrap items-center justify-between gap-3 border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/30 px-5 py-4" data-testid="pages-legal-warning">
      <p className="text-sm flex items-start gap-2"><AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" />Faltan datos del titular: en las páginas legales se ven como [Razón social], [NIF/CIF]… Rellénalos una vez y se actualizan en todas.</p>
      <Button size="sm" variant="outline" className="bg-card" asChild><Link to="/admin/ajustes#titular" data-testid="pages-legal-warning-link">Completar datos</Link></Button>
    </div>
  );
};

export default function Pages() {
  const { data = [] } = useQuery({ queryKey: ["admin-pages"], queryFn: () => get("/admin/pages") });
  const [tab, setTab] = useState("live");
  const actions = usePageActions();
  const live = data.filter((p) => !p.deleted_at);
  const trash = data.filter((p) => p.deleted_at);
  const liveRow = (p) => <Row key={p.id} p={p}><LiveActions p={p} actions={actions} /></Row>;
  const content = live.filter((p) => p.kind === "content");
  return (
    <div data-testid="admin-pages-page">
      <PageHeader kicker="Estructura" title="Páginas" description="Edita cualquier página de la web, crea páginas nuevas o elimínalas. Lo que eliminas va a la papelera y puedes recuperarlo.">
        <Button asChild><Link to="/admin/paginas/nueva" data-testid="pages-new-btn"><Plus /> Nueva página</Link></Button>
      </PageHeader>
      <div className="inline-flex gap-1 p-1 bg-card border border-border mb-8">
        {[["live", `Páginas (${live.length})`], ["trash", `Papelera (${trash.length})`]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm transition-colors ${tab === k ? "bg-foreground text-background font-semibold" : "hover:bg-muted"}`} data-testid={`pages-tab-${k}`}>{label}</button>
        ))}
      </div>
      {tab === "live" ? (
        <>
          <LegalWarning />
          <Group title="Páginas de la web" icon={FileText} items={content.filter((p) => p.footer_group !== "legal")} render={liveRow} testid="pages-group-content" />
          <Group title="Páginas legales" icon={Scale} items={content.filter((p) => p.footer_group === "legal")} render={liveRow} testid="pages-group-legal" />
          <Group title="Portada, plantillas y elementos fijos" icon={Lock} items={live.filter((p) => p.kind === "system")} render={liveRow} testid="pages-group-system" />
        </>
      ) : trash.length ? (
        <div className="bg-card border border-border" data-testid="pages-trash-list">{trash.map((p) => <Row key={p.id} p={p}><TrashActions p={p} actions={actions} /></Row>)}</div>
      ) : (
        <p className="text-muted-foreground py-10" data-testid="pages-trash-empty">La papelera está vacía.</p>
      )}
    </div>
  );
}
